/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import styled, { createGlobalStyle } from 'styled-components';
import type { HTMLAttributes } from 'react';
import type { AdditionalHeaderCellProps, CustomColumnProps, CustomRowProps } from '../../../components/table/types';
import type { AverageReportType, GroupType, SchoolViewType } from '../../types';
import type { ManageMetricsType } from '../../../forms/types';
import { Table } from '../../../components';
import { GreenTickStatus, TableHeader } from './ReportTable.components';
import { BUTTON_ICON_WRAPPER, TABLE_SORT } from '../../../util/data-id';
import HighlightCard from '../modules/HighlightCard';
import { yearAsDecimalToWeeks } from '../../../util/dates';

export type SchoolTableAccessors =
  | 'id'
  | 'name'
  | 'students'
  | 'unassessed'
  | 'maths_age_delta'
  | 'maths_age_improvements'
  | 'progressions'
  | 'quality'
  | 'consistency'
  | 'green_tick'
  | 'student_status'
  | 'time_span'
  | 'maths_age_at_start'
  | 'tutor_usage'
  | 'topic_challenge_usage'
  | 'progression_pass_rate'
  | 'exercise_mark_avg'
  | 'test_mark_avg'
  | 'age'
  | 'minutes'
  | 'highlighted';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;

interface Props extends HTMLProps {
  /**
   * Class report data.
   */
  schoolReport:
  {
    groups: (GroupType & AverageReportType)[],
    average: AverageReportType
  }
  /**
   * Highlighted element(s) id.
   */
  highlightedIds: number[] | null
  /**
   * Indicates the type of data to be used
   */
  classYearValue: SchoolViewType;
  showOthersData: boolean;
  metricsSettings: ManageMetricsType;
  onNameClick?: (id: number) => void;
}

const COLUMN_WIDTHS = {
  'name': 200,
};

const GlobalStyle = createGlobalStyle<{ columns: number }>`
  table {
    thead {
      tr:nth-child(2) {
        border: 1px solid black;
        border-right: 0;
        border-left: 0;
        font-weight: bold;
      }
    },
    [data-column='cell_students'],
    [data-column='cell_unassessed'],
    [data-column='cell_maths_age_delta'],
    [data-column='cell_maths_age_improvements'],
    [data-column='cell_progressions'],
    [data-column='cell_quality'],
    [data-column='cell_consistency'],
    [data-column='cell_green_tick'],
    [data-column='cell_student_status'],
    [data-column='cell_time_span'],
    [data-column='cell_maths_age_at_start'],
    [data-column='cell_tutor_usage'],
    [data-column='cell_topic_challenge_usage'],
    [data-column='cell_progression_pass_rate'],
    [data-column='cell_exercise_mark_avg'],
    [data-column='cell_test_mark_avg'],
    [data-column='cell_age'],
    [data-column='cell_minutes']
    {
      text-align: center;
      > div {
        text-align: center;
        width: 100%;
        white-space: nowrap;
      }
    },
    [data-column='cell_name'] {
      font-weight: normal !important;
    },
    [data-id='col_name'],
    [data-id='col_students'],
    [data-id='col_unassessed'],
    [data-id='col_maths_age_delta'],
    [data-id='col_maths_age_improvements'],
    [data-id='col_progressions'],
    [data-id='col_quality'],
    [data-id='col_consistency'],
    [data-id='col_green_tick'],
    [data-id='col_student_status'],
    [data-id='col_time_span'],
    [data-id='col_maths_age_at_start'],
    [data-id='col_tutor_usage'],
    [data-id='col_topic_challenge_usage'],
    [data-id='col_progression_pass_rate'],
    [data-id='col_exercise_mark_avg'],
    [data-id='col_test_mark_avg'],
    [data-id='col_age'],
    [data-id='col_minutes']
    {
      > div {
        height: 100%;
        flex-direction: column;
        display: grid;
        grid-template-rows: auto 30px;
        > div {
          grid-column-start: span 2;
        }
        [data-id='${BUTTON_ICON_WRAPPER}'] {
          grid-column-start: 1;
          justify-self: end;
          align-self: end;
        }
        [data-id='${TABLE_SORT}'] {
          grid-column-start: 2;
          justify-self: start;
          align-self: end;
        }
      }
    }
    [data-id^='col'] {
      width: 100%;
      @supports (-moz-appearance:none) {
        height: 120px !important;
      }
    }
    @media (min-width: 768px) {
      [data-id^='col'] {
        width: calc(100% / ${(props) => props.columns});
      }
    }
    [data-id='col_name'] {
      min-width: ${COLUMN_WIDTHS.name}px;
    }
  }
`;

const getAverage = (tutorUsageAverage: number, progressionAverage: number) => {
  if (tutorUsageAverage <= 0 || progressionAverage <= 0) {
    return '0';
  }
  return (tutorUsageAverage / progressionAverage).toFixed(1);
};

const SchoolReportTable = ({ schoolReport, highlightedIds, classYearValue, showOthersData, metricsSettings, onNameClick }: Props): JSX.Element => {
  const { translate } = useI18n();

  const columns = [
    {
      id: 'name',
      Header: <TableHeader
        title={''}
      />,
      Cell: ({ row }: { row: any }) => {
        const label = row.original.highlighted ?
          (<div className={'flex'}>
            <HighlightCard label={row.values.name} highlight margin={false} hideEmptyPlaceholder />
          </div>)
          : row.values.name;
        const onClickAllowed = onNameClick?.(parseInt(row.original.id));
        return onClickAllowed ? (<button className={'underline hover:no-underline text-left'} onClick={onClickAllowed}>{label}</button>) : label;
      },
      accessor: 'name',
      sticky: 'left',
    },
    {
      id: 'students',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.STUDENTS.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.STUDENTS.info'),
      accessor: 'students',
    },
    {
      id: 'unassessed',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.UNASSESSED.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.UNASSESSED.info'),
      accessor: 'unassessed',
    },
    {
      id: 'age',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.AGE.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.AGE.info'),
      accessor: 'age',
    },
    {
      id: 'maths_age_at_start',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.MATHS_AGE_AT_START.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.MATHS_AGE_AT_START.info'),
      accessor: 'maths_age_at_start',
    },
    {
      id: 'maths_age_delta',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.MATHS_AGE_DELTA.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.YEARS')}
      />,
      tooltipMessage: translate('pages.SCHOOL_REPORTS.SECTIONS.MATHS_AGE_DELTA.info'),
      accessor: 'maths_age_delta',
      sortType: (a, b, id) => Number(a.values[id]) - Number(b.values[id]),
    },
    {
      id: 'maths_age_improvements',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.IMPROVEMENT.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.WEEKS')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.IMPROVEMENT.info'),
      accessor: 'maths_age_improvements',
      sortType: (a, b, id) => Number(a.values[id]) - Number(b.values[id]),
    },
    {
      id: 'time_span',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.TIME_SPAN.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.WEEKS')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.TIME_SPAN.info'),
      accessor: 'time_span',
    },
    {
      id: 'progressions',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.PROGRESSIONS.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.WEEK')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.PROGRESSIONS.info'),
      accessor: 'progressions',
    },
    {
      id: 'quality',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.QUALITY_LEARNING_TIME.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.MINS_PER_WEEK')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.QUALITY_LEARNING_TIME.info'),
      accessor: 'quality',
    },
    {
      id: 'tutor_usage',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.TUTOR_USAGE.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.MINS_PER_WEEK')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.TUTOR_USAGE.info'),
      accessor: 'tutor_usage',
    },
    {
      id: 'topic_challenge_usage',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.TOPIC_CHALLENGE_USAGE.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.MINS_PER_WEEK')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.TOPIC_CHALLENGE_USAGE.info'),
      accessor: 'topic_challenge_usage',
    },
    {
      id: 'minutes',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.MINUTES.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.MINUTES.info'),
      accessor: 'minutes',
    },
    {
      id: 'green_tick',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.GREEN_TICK_STATUS.label')}
      />,
      Cell: ({ row }:{ row: any }) =>
        <GreenTickStatus
          status={row.original.green_tick}
        />
      ,
      tooltipMessage: translate('pages.REPORTS.HEADERS.GREEN_TICK_STATUS.info'),
      accessor: 'green_tick',
    },
    {
      id: 'progression_pass_rate',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.PROGRESSION_PASS_RATE.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.PROGRESSION_PASS_RATE.info'),
      accessor: 'progression_pass_rate',
    },
    {
      id: 'exercise_mark_avg',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.EXERCISE_MARK_AVG.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.EXERCISE_MARK_AVG.info'),
      accessor: 'exercise_mark_avg',
    },
    {
      id: 'test_mark_avg',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.TEST_MARK_AVG.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.TEST_MARK_AVG.info'),
      accessor: 'test_mark_avg',
    },
  ] as (CustomColumnProps<SchoolTableAccessors> & AdditionalHeaderCellProps)[];

  const filteredColumns = columns.filter((c) => 'name' === c.id
    || metricsSettings.default_settings.includes(c.id!)
    || metricsSettings.additional_settings.includes(c.id!),
  ) as (CustomColumnProps<SchoolTableAccessors> & AdditionalHeaderCellProps)[];

  const getFormattedData = (): CustomRowProps<SchoolTableAccessors>[]  => {
    return schoolReport.groups.map((cl) => {
      return {
        id: cl.id.toString(),
        name: cl.fullName,
        students: cl.totalStudentsNum,
        unassessed: cl.unassessedStudents,
        maths_age_delta: cl.chartsData?.mathsAgeDelta.toFixed(2),
        maths_age_improvements: cl.mathsAgeImprovementAverage.toFixed(1),
        progressions: cl.progressionAverage.toFixed(2),
        quality: cl.qualityLearningTimeAverage,
        consistency: cl.consistencyAverage.toFixed(2),
        green_tick: cl.tickStatusAverage,
        student_status: cl.studentStatusAverage,
        time_span: yearAsDecimalToWeeks(cl.timeSpanAverage),
        maths_age_at_start: cl.mathsAgeAtStartAverage.toFixed(2),
        tutor_usage: cl.tutorUsageAverage,
        topic_challenge_usage: cl.topicChallengeUsageAverage,
        progression_pass_rate: cl.progressionPassRateAverage,
        exercise_mark_avg: cl.exerciseMarkAverage,
        test_mark_avg: cl.testMarkAverage,
        age: cl.actualAgeAverage.toFixed(1),
        minutes: getAverage(cl.tutorUsageAverage, cl.progressionAverage),
        highlighted: highlightedIds?.includes(cl.id),
      };
    });
  };

  const [ studentData, setStudentData ] = useState(getFormattedData());

  useEffect(() => {
    setStudentData(getFormattedData());
  }, [ highlightedIds, classYearValue, schoolReport ]);

  const getStyle = (index: number) : Record<string, unknown> => {
    if (studentData[index].highlighted) return {
      style: {
        border:  '1px solid black',
        borderRight: '0px',
        borderLeft: '0',
        fontWeight: 'bold',
      },
    };

    if (!showOthersData) return {
      style: {
        display: 'none',
      },
    };

    return {};
  };

  return (
    <StyledWrapper className={'container bg-white rounded-default shadow'}>
      <GlobalStyle columns={filteredColumns.length}/>
      <Table
        id={'school_report'}
        cols={filteredColumns}
        content={studentData}
        sticky
        getRowProps={(row) => (
          getStyle(row.index)
        )}
        initialState={{
          sortBy: [
            {
              id: 'name',
              desc: false,
            },
          ],
        }}
      />
    </StyledWrapper>
  );
};

const StyledWrapper = styled('div')`
  max-width: initial;
  width: fit-content;
`;

SchoolReportTable.displayName = 'SchoolReportTable';

export default SchoolReportTable;
